import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Upload, X, Check, Image as ImageIcon, AlertCircle, ExternalLink, Star } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One-click claim editor — opened from outreach emails.
 * Token in URL identifies app + recipient email. No login required.
 * On submit, edits land in `app_claim_edits` for admin approval.
 */
const ClaimEditorPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const logoInputRef = useRef(null);
  const screenshotInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/claim/${token}`)
      .then((r) => {
        setData(r.data);
        const a = r.data.app || {};
        const e = r.data.existing_edit?.proposed || {};
        setForm({
          tagline: e.tagline ?? a.tagline ?? "",
          description: e.description ?? a.description ?? "",
          website: e.website ?? a.website ?? "",
          country: e.country ?? a.country ?? "",
          founded: e.founded ?? a.founded ?? "",
          logo: e.logo ?? a.logo ?? "",
          screenshots: e.screenshots ?? a.screenshots ?? [],
          plans: e.plans ?? a.plans ?? [],
          contact_name: "",
          contact_role: "",
          note: "",
        });
      })
      .catch((err) => setError(err.response?.data?.detail || "Could not load claim form"))
      .finally(() => setLoading(false));
  }, [token]);

  const uploadFile = async (file, isLogo = false) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/claim/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (isLogo) {
        setForm((f) => ({ ...f, logo: r.data.url }));
      } else {
        setForm((f) => ({ ...f, screenshots: [...(f.screenshots || []), r.data.url].slice(0, 6) }));
      }
    } catch (e) {
      alert(`Upload failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const removeScreenshot = (idx) => {
    setForm((f) => ({ ...f, screenshots: f.screenshots.filter((_, i) => i !== idx) }));
  };

  const makePrimaryScreenshot = (idx) => {
    setForm((f) => {
      if (idx <= 0 || !f.screenshots?.[idx]) return f;
      const list = [...f.screenshots];
      const [picked] = list.splice(idx, 1);
      return { ...f, screenshots: [picked, ...list] };
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.founded === "" || payload.founded === null) delete payload.founded;
      else payload.founded = parseInt(payload.founded, 10);
      await axios.post(`${API}/claim/${token}/submit`, payload);
      setSubmitted(true);
    } catch (e) {
      alert(`Submit failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  if (loading) return <main className="hac-container py-32 text-center text-[#94A3B8]"><Loader2 className="inline animate-spin" /></main>;
  if (error) return (
    <main className="hac-container py-20 text-center" data-testid="claim-error">
      <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
      <h1 className="font-heading text-3xl text-[#0F172A] mb-3">Claim link not available</h1>
      <p className="text-[#64748B] mb-6">{error}</p>
      <Link to="/" className="hac-btn-primary">Back to home</Link>
    </main>
  );

  if (submitted) return (
    <main className="hac-container py-20 text-center max-w-xl mx-auto" data-testid="claim-submitted">
      <div className="inline-block bg-emerald-100 text-emerald-700 rounded-full p-4 mb-4"><Check size={32} /></div>
      <h1 className="font-heading text-3xl text-[#0F172A] mb-3">Thank you!</h1>
      <p className="text-[#64748B] mb-6">
        Your updates for <strong>{data.app?.name}</strong> have been submitted for review. Our team will verify and publish them within 1-2 business days.
        We&apos;ll email <strong>{data.email}</strong> once it goes live.
      </p>
      <Link to={`/apps/${data.app?.slug}`} className="hac-btn-secondary">View your listing</Link>
    </main>
  );

  const app = data.app;
  return (
    <main className="hac-container py-12 max-w-3xl mx-auto" data-testid="claim-editor">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#EA580C] font-bold mb-2">Owner claim · one-time link</div>
        <h1 className="font-heading text-4xl text-[#0F172A] mb-2">Edit your listing for {app.name}</h1>
        <p className="text-[#64748B] leading-relaxed">
          You&apos;re editing the public profile of <a href={`/apps/${app.slug}`} target="_blank" rel="noreferrer" className="text-[#2563EB] underline">horseappcompare.com/apps/{app.slug}</a>.
          Save when ready — an admin will verify your changes and publish them. Reach: <strong>{data.email}</strong>
        </p>
      </div>

      {/* Logo upload */}
      <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6 mb-6">
        <h2 className="font-bold text-[#0F172A] mb-3">Logo</h2>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-xl bg-[#F1F5F9] border border-[#E2E8F0] overflow-hidden flex items-center justify-center">
            {form.logo ? <img src={form.logo} alt="Logo" className="w-full h-full object-cover" /> : <ImageIcon size={28} className="text-[#94A3B8]" />}
          </div>
          <div>
            <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], true)} data-testid="claim-logo-input" />
            <button onClick={() => logoInputRef.current?.click()} disabled={busy} className="hac-btn-secondary" data-testid="claim-logo-upload">
              <Upload size={12} /> {form.logo ? "Replace logo" : "Upload logo"}
            </button>
            <p className="text-xs text-[#94A3B8] mt-2">PNG/JPG, square, max 8MB</p>
          </div>
        </div>
      </section>

      {/* Screenshots */}
      <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6 mb-6">
        {!app.premium && (
          <a
            href="/advertise#premium"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 mb-4 bg-gradient-to-r from-amber-50 to-amber-100/60 hover:from-amber-100 hover:to-amber-200/60 border border-amber-300 rounded-xl px-4 py-2.5 transition-colors group"
            data-testid="claim-premium-upsell"
          >
            <span className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-400 to-amber-500 text-amber-900 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0">
              <Star size={10} className="fill-current" /> Premium
            </span>
            <div className="text-xs text-amber-900 leading-snug flex-1">
              Pin up to <strong>3 hero screenshots</strong> + featured slot on category pages.
            </div>
            <span className="text-[11px] font-bold text-amber-900 group-hover:text-amber-950 shrink-0 inline-flex items-center gap-0.5">
              Upgrade <ExternalLink size={11} />
            </span>
          </a>
        )}
        <h2 className="font-bold text-[#0F172A] mb-1">Screenshots <span className="text-xs font-normal text-[#94A3B8]">({form.screenshots?.length || 0}/6)</span></h2>
        <p className="text-xs text-[#64748B] mb-4">
          The <strong className="text-[#0F172A]">first screenshot</strong> appears as your preview thumbnail in the directory listing — pick the one that best represents your app.
        </p>
        <div className="grid grid-cols-3 gap-3 mb-4">
          {(form.screenshots || []).map((s, i) => (
            <div key={s} className={`relative aspect-[9/16] rounded-lg overflow-hidden border bg-[#F8FAFC] group ${i === 0 ? "border-[#2563EB] ring-2 ring-[#2563EB]/30" : "border-[#E2E8F0]"}`}>
              <img src={s} alt={`Screenshot ${i+1}`} className="w-full h-full object-cover" />
              {i === 0 && (
                <div className="absolute top-1 left-1 bg-[#2563EB] text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1" data-testid={`claim-primary-badge-${i}`}>
                  <Star size={9} className="fill-current" /> Primary
                </div>
              )}
              <button onClick={() => removeScreenshot(i)} className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 hover:bg-red-500" data-testid={`claim-remove-screenshot-${i}`}><X size={12} /></button>
              {i > 0 && (
                <button
                  onClick={() => makePrimaryScreenshot(i)}
                  className="absolute bottom-1 left-1 right-1 bg-white/95 hover:bg-[#2563EB] hover:text-white text-[#0F172A] text-[10px] font-bold uppercase tracking-wider py-1 rounded inline-flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`claim-make-primary-${i}`}
                >
                  <Star size={10} /> Set as primary
                </button>
              )}
            </div>
          ))}
          {(form.screenshots?.length || 0) < 6 && (
            <button onClick={() => screenshotInputRef.current?.click()} disabled={busy} className="aspect-[9/16] rounded-lg border-2 border-dashed border-[#CBD5E1] hover:border-[#2563EB] hover:bg-blue-50 flex flex-col items-center justify-center text-[#64748B] text-xs gap-1" data-testid="claim-screenshot-add">
              <Upload size={20} />
              <span>Add</span>
            </button>
          )}
        </div>
        <input ref={screenshotInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], false)} />
      </section>

      {/* Tagline */}
      <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6 mb-6 space-y-4">
        <h2 className="font-bold text-[#0F172A]">Tagline</h2>
        <div>
          <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} maxLength={120} placeholder="One-line description (English)" className="hac-input w-full" data-testid="claim-tagline-en" />
          <p className="text-[11px] text-[#94A3B8] mt-1">English only — auto-translated to 22 languages.</p>
        </div>
      </section>

      {/* Description */}
      <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6 mb-6 space-y-4">
        <h2 className="font-bold text-[#0F172A]">Description</h2>
        <div>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={5} maxLength={1500} placeholder="What does your app do? Who is it for? What makes it unique? (English)" className="hac-input w-full" data-testid="claim-desc-en" />
        </div>
      </section>

      {/* Basic info */}
      <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6 mb-6 grid sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-wide font-bold text-[#64748B] mb-1">Website</label>
          <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." className="hac-input w-full" data-testid="claim-website" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wide font-bold text-[#64748B] mb-1">Country</label>
          <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="hac-input w-full" data-testid="claim-country" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wide font-bold text-[#64748B] mb-1">Founded year</label>
          <input value={form.founded} onChange={(e) => setForm({ ...form, founded: e.target.value })} type="number" min="1980" max="2030" className="hac-input w-full" data-testid="claim-founded" />
        </div>
      </section>

      {/* Optional contact details */}
      <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6 mb-8 space-y-4">
        <h2 className="font-bold text-[#0F172A]">Your details <span className="text-xs font-normal text-[#94A3B8]">(helps the admin verify you)</span></h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Your name" className="hac-input w-full" data-testid="claim-contact-name" />
          <input value={form.contact_role} onChange={(e) => setForm({ ...form, contact_role: e.target.value })} placeholder="Role (e.g. Founder, Marketing)" className="hac-input w-full" data-testid="claim-contact-role" />
        </div>
        <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} maxLength={500} placeholder="Any note for the admin (optional)" className="hac-input w-full" data-testid="claim-note" />
      </section>

      <div className="flex flex-wrap gap-3 items-center justify-between bg-[#0F172A] text-white rounded-2xl p-5">
        <div className="text-sm">
          Ready to submit? Edits go to admin review and publish once approved.
        </div>
        <button onClick={submit} disabled={busy} className="bg-[#EA580C] hover:bg-[#C2410C] text-white px-6 py-3 rounded-full font-bold inline-flex items-center gap-2" data-testid="claim-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Submit for review
        </button>
      </div>
    </main>
  );
};

export default ClaimEditorPage;
